import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Download, KeyRound, Loader2, Pencil, Plus, Trash2, Upload } from "lucide-react";
import { ROLE_LABELS, type BulkResult, type BulkUserRow, type CreateUserInput, type HospitalDto, type Role, type UserDto } from "@ambulance/shared";
import { useApi } from "../api";
import { useMe } from "../hooks/useMe";
import { downloadCsv, parseCsv, rowsToCsv } from "../lib/csv";
import { btnPrimary, btnSecondary, ErrorBox, Field, Forbidden, inputCls, Modal, Spinner } from "../components/ui";

export function UsersPage() {
  const { me, isLoading } = useMe();
  const api = useApi();
  const qc = useQueryClient();
  const users = useQuery({ queryKey: ["users"], queryFn: () => api.get<UserDto[]>("/api/v1/users"), enabled: Boolean(api.token) });
  const hospitals = useQuery({ queryKey: ["hospitals"], queryFn: () => api.get<HospitalDto[]>("/api/v1/hospitals"), enabled: Boolean(api.token) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["users"] });

  const createUser = useMutation({ mutationFn: (d: CreateUserInput) => api.post<UserDto>("/api/v1/users", d), onSuccess: invalidate });
  const importUsers = useMutation({ mutationFn: (rows: BulkUserRow[]) => api.post<BulkResult>("/api/v1/users/bulk", { users: rows }), onSuccess: invalidate });
  const setRole = useMutation({ mutationFn: ({ id, role }: { id: string; role: Role }) => api.put<UserDto>(`/api/v1/users/${id}/role`, { role }), onSuccess: invalidate });
  const updateMeta = useMutation({ mutationFn: ({ id, name, badgeNo }: { id: string; name: string; badgeNo: string }) => api.patch<UserDto>(`/api/v1/users/${id}`, { name, badgeNo }), onSuccess: invalidate });
  const assign = useMutation({ mutationFn: ({ id, hospitalIds }: { id: string; hospitalIds: string[] }) => api.put<UserDto>(`/api/v1/users/${id}/hospitals`, { hospitalIds }), onSuccess: invalidate });
  const deleteUser = useMutation({ mutationFn: (id: string) => api.del<void>(`/api/v1/users/${id}`), onSuccess: invalidate });
  const resetPwd = useMutation({ mutationFn: ({ id, newPassword }: { id: string; newPassword: string }) => api.post<void>(`/api/v1/users/${id}/password-reset`, { newPassword }) });

  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [assigningFor, setAssigningFor] = useState<string | null>(null);
  const [editingFor, setEditingFor] = useState<string | null>(null);
  const [resettingFor, setResettingFor] = useState<string | null>(null);

  if (isLoading) return <Spinner />;
  if (!me || me.role !== "super_admin") return <Forbidden />;

  function exportUsers() {
    if (!users.data) return;
    const csv = rowsToCsv(["Name", "Badge No", "Email"], users.data.map((u) => [u.name ?? "", u.badgeNo ?? "", u.email]));
    downloadCsv(`users-${new Date().toISOString().slice(0, 10)}.csv`, csv);
  }
  const list = users.data ?? [];
  const hospitalList = hospitals.data ?? [];

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">User Management</h1>
        <div className="flex items-center gap-2">
          <button type="button" onClick={exportUsers} disabled={list.length === 0} className={btnSecondary}><Download className="w-4 h-4" /> Export</button>
          <button type="button" onClick={() => setShowImport(true)} className={btnSecondary}><Upload className="w-4 h-4" /> Bulk import</button>
          <button type="button" onClick={() => setShowCreate(true)} className={btnPrimary}><Plus className="w-4 h-4" /> Create user</button>
        </div>
      </div>

      {users.isPending ? (
        <Spinner />
      ) : users.isError ? (
        <ErrorBox>{users.error.message}</ErrorBox>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-600">
                  <th className="px-4 py-3">User</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Assigned Hospitals</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {list.map((u) => (
                  <tr key={u.id}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{u.name || "—"}</div>
                      <div className="text-sm text-slate-500">{u.email}</div>
                      {u.badgeNo && <div className="text-xs text-slate-400">Badge: {u.badgeNo}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={u.role}
                        disabled={u.id === me.id || setRole.isPending}
                        onChange={(e) => setRole.mutateAsync({ id: u.id, role: e.target.value as Role }).catch((err) => alert((err as Error).message))}
                        className="text-sm rounded border border-slate-300 px-2 py-1 disabled:bg-slate-50 disabled:text-slate-400"
                      >
                        <option value="supervisor">Supervisor</option>
                        <option value="super_admin">Super Admin</option>
                        <option value="paramedic">Paramedic</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      {u.role === "super_admin" ? (
                        <span className="text-xs text-slate-500 italic">All hospitals</span>
                      ) : u.role === "paramedic" ? (
                        <span className="text-xs text-slate-400 italic">Not applicable</span>
                      ) : (
                        <button type="button" onClick={() => setAssigningFor(u.id)} className="text-sm text-[var(--hmg-red)] hover:underline inline-flex items-center gap-1">
                          <Building2 className="w-3.5 h-3.5" />
                          {u.hospitals.length === 0 ? "Assign..." : `${u.hospitals.length} assigned`}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button type="button" onClick={() => setEditingFor(u.id)} className="text-slate-600 hover:text-slate-900 p-1.5 rounded" title="Edit name & badge"><Pencil className="w-4 h-4" /></button>
                      <button type="button" onClick={() => setResettingFor(u.id)} className="text-slate-600 hover:text-slate-900 p-1.5 rounded" title="Reset password"><KeyRound className="w-4 h-4" /></button>
                      {u.id !== me.id && (
                        <button
                          type="button"
                          onClick={async () => {
                            if (confirm(`Delete user ${u.email}? This cannot be undone.`)) {
                              try {
                                await deleteUser.mutateAsync(u.id);
                              } catch (e) {
                                alert((e as Error).message);
                              }
                            }
                          }}
                          className="text-red-600 hover:text-red-700 p-1.5 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showCreate && <CreateUserModal onClose={() => setShowCreate(false)} hospitals={hospitalList} create={(d) => createUser.mutateAsync(d).then(() => undefined)} />}
      {showImport && <BulkImportUsersModal onClose={() => setShowImport(false)} runImport={(rows) => importUsers.mutateAsync(rows)} />}
      {assigningFor && (
        <AssignHospitalsModal user={list.find((u) => u.id === assigningFor)} hospitals={hospitalList} onClose={() => setAssigningFor(null)} onSave={(ids) => assign.mutateAsync({ id: assigningFor, hospitalIds: ids }).then(() => undefined)} />
      )}
      {editingFor && <EditUserModal user={list.find((u) => u.id === editingFor)} onClose={() => setEditingFor(null)} onSave={(name, badgeNo) => updateMeta.mutateAsync({ id: editingFor, name, badgeNo }).then(() => undefined)} />}
      {resettingFor && <ResetPasswordModal onClose={() => setResettingFor(null)} onSave={(pwd) => resetPwd.mutateAsync({ id: resettingFor, newPassword: pwd })} />}
    </div>
  );
}

function EditUserModal({ user, onClose, onSave }: { user?: UserDto; onClose: () => void; onSave: (name: string, badgeNo: string) => Promise<void> }) {
  const [name, setName] = useState(user?.name ?? "");
  const [badgeNo, setBadgeNo] = useState(user?.badgeNo ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <Modal title={<><Pencil className="w-5 h-5" /> Edit user</>} subtitle={user?.email} onClose={onClose}>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setErr(null);
          setSaving(true);
          try {
            await onSave(name.trim(), badgeNo.trim());
            onClose();
          } catch (e) {
            setErr((e as Error).message ?? "Failed to save changes");
          } finally {
            setSaving(false);
          }
        }}
        className="space-y-3"
      >
        <Field label="Full name"><input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} /></Field>
        <Field label="Badge No"><input value={badgeNo} onChange={(e) => setBadgeNo(e.target.value)} className={inputCls} /></Field>
        {err && <ErrorBox>{err}</ErrorBox>}
        <div className="flex gap-2 pt-2">
          <button type="submit" disabled={saving} className={`${btnPrimary} flex-1 justify-center`}>{saving && <Loader2 className="w-4 h-4 animate-spin" />}Save</button>
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200">Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

function CreateUserModal({ onClose, hospitals, create }: { onClose: () => void; hospitals: HospitalDto[]; create: (d: CreateUserInput) => Promise<void> }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<Role>("supervisor");
  const [badgeNo, setBadgeNo] = useState("");
  const [hospitalIds, setHospitalIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <Modal title="Create user" onClose={onClose}>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setErr(null);
          setSaving(true);
          try {
            await create({ email, password, name: name || undefined, role, badgeNo: badgeNo || undefined, hospitalIds: role === "supervisor" ? hospitalIds : undefined });
            onClose();
          } catch (e) {
            setErr((e as Error).message ?? "Failed to create user");
          } finally {
            setSaving(false);
          }
        }}
        className="space-y-3"
      >
        <Field label="Email *"><input type="email" required autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="email" value={email} onChange={(e) => setEmail(e.target.value.trim().toLowerCase())} className={inputCls} /></Field>
        <Field label="Temporary password * (min 8)"><input type="text" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className={`${inputCls} font-mono`} /></Field>
        <Field label="Full name"><input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} /></Field>
        <Field label="Badge No"><input value={badgeNo} onChange={(e) => setBadgeNo(e.target.value)} className={inputCls} /></Field>
        <Field label="Role">
          <select value={role} onChange={(e) => setRole(e.target.value as Role)} className={inputCls}>
            {(Object.keys(ROLE_LABELS) as Role[]).map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
          </select>
        </Field>
        {role === "supervisor" && hospitals.length > 0 && (
          <Field label="Assign hospitals">
            <div className="border border-slate-300 rounded-lg p-2 max-h-40 overflow-y-auto">
              {hospitals.map((h) => (
                <label key={h.id} className="flex items-center gap-2 py-1 text-sm hover:bg-slate-50 px-1 rounded cursor-pointer">
                  <input type="checkbox" checked={hospitalIds.includes(h.id)} onChange={(e) => setHospitalIds(e.target.checked ? [...hospitalIds, h.id] : hospitalIds.filter((id) => id !== h.id))} />
                  {h.name}
                </label>
              ))}
            </div>
          </Field>
        )}
        <p className="text-xs text-slate-500">The user signs in with the temporary password and is asked to set a new one immediately.</p>
        {err && <ErrorBox>{err}</ErrorBox>}
        <div className="flex gap-2 pt-2">
          <button type="submit" disabled={saving} className={`${btnPrimary} flex-1 justify-center`}>{saving && <Loader2 className="w-4 h-4 animate-spin" />}Create</button>
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200">Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

function AssignHospitalsModal({ user, hospitals, onClose, onSave }: { user?: UserDto; hospitals: HospitalDto[]; onClose: () => void; onSave: (ids: string[]) => Promise<void> }) {
  const [selected, setSelected] = useState<string[]>((user?.hospitals ?? []).map((h) => h.id));
  const [saving, setSaving] = useState(false);
  return (
    <Modal title="Assign hospitals" subtitle={user?.name ?? user?.email} onClose={onClose}>
      {hospitals.length === 0 ? (
        <p className="text-sm text-slate-600">No hospitals exist yet.</p>
      ) : (
        <div className="border border-slate-300 rounded-lg p-2 max-h-72 overflow-y-auto">
          {hospitals.map((h) => (
            <label key={h.id} className="flex items-center gap-2 py-1.5 text-sm hover:bg-slate-50 px-2 rounded cursor-pointer">
              <input type="checkbox" checked={selected.includes(h.id)} onChange={(e) => setSelected(e.target.checked ? [...selected, h.id] : selected.filter((id) => id !== h.id))} />
              {h.name}
            </label>
          ))}
        </div>
      )}
      <div className="flex gap-2 pt-4">
        <button
          type="button"
          disabled={saving}
          onClick={async () => {
            setSaving(true);
            try {
              await onSave(selected);
              onClose();
            } catch (e) {
              alert((e as Error).message);
            } finally {
              setSaving(false);
            }
          }}
          className={`${btnPrimary} flex-1 justify-center`}
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}Save
        </button>
        <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200">Cancel</button>
      </div>
    </Modal>
  );
}

function ResetPasswordModal({ onClose, onSave }: { onClose: () => void; onSave: (pwd: string) => Promise<void> }) {
  const [pwd, setPwd] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <Modal title={<><KeyRound className="w-5 h-5" /> Reset password</>} onClose={onClose} size="sm">
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setErr(null);
          setSaving(true);
          try {
            await onSave(pwd);
            onClose();
          } catch (e) {
            setErr((e as Error).message ?? "Failed to reset password");
          } finally {
            setSaving(false);
          }
        }}
      >
        <Field label="Temporary password (min 8)"><input type="text" required minLength={8} value={pwd} onChange={(e) => setPwd(e.target.value)} className={`${inputCls} font-mono`} /></Field>
        <p className="mt-2 text-xs text-slate-500">The user is signed out everywhere and must set a new password at the next sign-in.</p>
        {err && <div className="mt-2"><ErrorBox>{err}</ErrorBox></div>}
        <div className="flex gap-2 pt-4">
          <button type="submit" disabled={saving} className={`${btnPrimary} flex-1 justify-center`}>{saving && <Loader2 className="w-4 h-4 animate-spin" />}Reset</button>
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200">Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

function BulkImportUsersModal({ onClose, runImport }: { onClose: () => void; runImport: (rows: BulkUserRow[]) => Promise<BulkResult> }) {
  const [parsed, setParsed] = useState<BulkUserRow[] | null>(null);
  const [fileName, setFileName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<BulkResult | null>(null);

  function downloadTemplate() {
    downloadCsv("users-template.csv", "Email,Password,Name,Role,Badge No,Hospitals\njane.doe@hmg.com,ChangeMe123,Jane Doe,supervisor,B123,Dr Sulaiman Al Habib - Olaya;Dr Sulaiman Al Habib - Qassim\nahmed.ali@hmg.com,ChangeMe123,Ahmed Ali,paramedic,B456,\n");
  }
  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    setError(null);
    setResult(null);
    setParsed(null);
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    try {
      const rows = parseCsv(await file.text()).filter((r) => r.some((c) => c.trim() !== ""));
      if (rows.length < 1) throw new Error("The file looks empty.");
      const header = rows[0].map((h) => h.trim().toLowerCase());
      const col = (...names: string[]) => header.findIndex((h) => names.includes(h));
      const emailIdx = col("email");
      const passwordIdx = col("password");
      const nameIdx = col("name", "full name");
      const roleIdx = col("role");
      const badgeIdx = col("badge no", "badgeno", "badge");
      const hospitalsIdx = col("hospitals", "hospital");
      if (emailIdx === -1 || passwordIdx === -1) throw new Error('Template needs "Email" and "Password" columns.');
      const list: BulkUserRow[] = rows
        .slice(1)
        .map((r) => ({
          email: (r[emailIdx] ?? "").trim(),
          password: (r[passwordIdx] ?? "").trim(),
          name: nameIdx >= 0 ? (r[nameIdx] ?? "").trim() : "",
          role: roleIdx >= 0 ? (r[roleIdx] ?? "").trim() : "",
          badgeNo: badgeIdx >= 0 ? (r[badgeIdx] ?? "").trim() : "",
          hospitals: hospitalsIdx >= 0 ? (r[hospitalsIdx] ?? "").trim() : "",
        }))
        .filter((u) => u.email !== "");
      if (list.length === 0) throw new Error("No rows with an email were found.");
      setParsed(list);
    } catch (err) {
      setError((err as Error).message ?? "Could not read the file.");
    }
  }
  async function doImport() {
    if (!parsed) return;
    setBusy(true);
    setError(null);
    try {
      setResult(await runImport(parsed));
    } catch (err) {
      setError((err as Error).message ?? "Import failed.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal title={<><Upload className="w-5 h-5" /> Bulk import users</>} onClose={onClose} size="lg">
      {result ? (
        <div className="space-y-4">
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm p-3">
            Created <span className="font-semibold">{result.created}</span> user{result.created === 1 ? "" : "s"}.
            {result.skipped > 0 && <> Skipped <span className="font-semibold">{result.skipped}</span>.</>}
          </div>
          {result.errors.length > 0 && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs p-3 max-h-48 overflow-y-auto space-y-1">
              {result.errors.map((er, i) => <div key={i}>• {er}</div>)}
            </div>
          )}
          <button type="button" onClick={onClose} className="w-full rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-2">Done</button>
        </div>
      ) : (
        <div className="space-y-4">
          <ol className="text-sm text-slate-600 list-decimal pl-5 space-y-1">
            <li>Download the template.</li>
            <li>Fill one user per row. <strong>Email</strong> and <strong>Password</strong> (min 8 chars) are required.</li>
            <li><strong>Role</strong> is supervisor, super_admin, or paramedic (defaults to supervisor).</li>
            <li><strong>Hospitals</strong> (supervisors only): hospital names separated by a semicolon <code>;</code>, matched to existing hospitals.</li>
            <li>Save as CSV and upload it below.</li>
          </ol>
          <button type="button" onClick={downloadTemplate} className="w-full rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold py-2 inline-flex items-center justify-center gap-2"><Download className="w-4 h-4" /> Download template (CSV)</button>
          <label className="block">
            <span className="block text-sm font-medium text-slate-700 mb-1">Upload filled CSV</span>
            <input type="file" accept=".csv,text/csv" onChange={onFile} className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-sm file:font-semibold hover:file:bg-slate-200" />
          </label>
          {parsed && !error && (
            <div className="rounded-lg bg-blue-50 border border-blue-200 text-blue-800 text-sm p-3"><span className="font-semibold">{parsed.length}</span> user{parsed.length === 1 ? "" : "s"} found in <span className="font-mono">{fileName}</span>, ready to import.</div>
          )}
          {error && <ErrorBox>{error}</ErrorBox>}
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={doImport} disabled={busy || !parsed} className={`${btnPrimary} flex-1 justify-center`}>{busy && <Loader2 className="w-4 h-4 animate-spin" />}Import{parsed ? ` ${parsed.length}` : ""}</button>
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200">Cancel</button>
          </div>
        </div>
      )}
    </Modal>
  );
}
